import { CodeBlock } from '../../components/developers/CodeBlock';

export default function CliReference() {
  return (
    <article className="prose prose-slate max-w-none">
      <h1>CLI Reference</h1>
      <p className="lead">
        The Opair CLI (<code>opairctl</code>) provides command-line access to manage policies,
        bundles, deployments, and platform configuration.
      </p>

      <h2>Installation</h2>
      <CodeBlock
        language="bash"
        code={`npm install -g @opair/cli

# Verify installation
opairctl --version`}
      />

      <h2>Configuration</h2>
      <p>Set up your CLI credentials and context:</p>
      <CodeBlock
        language="bash"
        code={`# Interactive login
opairctl login

# Or set API key directly
opairctl config set api-key YOUR_API_KEY
opairctl config set org-id YOUR_ORG_ID

# Context management
opairctl config set-context production --api-url https://api.opair.example.com --org-id org-123
opairctl config set-context staging --api-url https://staging-api.opair.example.com --org-id org-456
opairctl config use-context production
opairctl config current-context`}
      />

      <h2>Commands Reference</h2>

      <h3>Context &amp; Config Management</h3>
      <CodeBlock
        title="opairctl config"
        language="bash"
        code={`# Set named context
opairctl config set-context <name> --api-url <url> --org-id <id> [--api-key <key>]

# Switch active context
opairctl config use-context <name>

# Show current context
opairctl config current-context

# List all contexts
opairctl config get-contexts

# Export full organization config (YAML/JSON)
opairctl config export --format yaml --output org-config.yaml
opairctl config export --format json --output org-config.json

# Import organization config
opairctl config import --file org-config.yaml
opairctl config import --file org-config.yaml --dry-run     # Preview changes
opairctl config import --file org-config.yaml --replace      # Replace mode (overwrites)`}
      />

      <h3>Resource Management (apply)</h3>
      <CodeBlock
        title="opairctl apply"
        language="bash"
        code={`# Apply a YAML/JSON resource definition
opairctl apply -f policy.yaml
opairctl apply -f workload.json
opairctl apply -f ./resources/       # Apply all files in directory

# Example policy.yaml:
# apiVersion: opair/v1
# kind: Policy
# metadata:
#   name: content-moderation
# spec:
#   content: "package contentmod\\ndefault allow := false"`}
      />

      <h3>Policy Management</h3>
      <CodeBlock
        title="opairctl policy"
        language="bash"
        code={`# List all policies
opairctl policy list

# Get policy details
opairctl policy get content-moderation-v1

# Create a new policy
opairctl policy create --name "my-policy" --file policy.opair

# Update an existing policy
opairctl policy update my-policy --file policy.opair

# Delete a policy
opairctl policy delete my-policy --confirm

# Validate policy syntax
opairctl policy validate policy.opair

# View policy version history
opairctl policy versions my-policy

# Diff two versions
opairctl policy diff my-policy --from v1 --to v2`}
      />

      <h3>Bundle Operations</h3>
      <CodeBlock
        title="opairctl bundle"
        language="bash"
        code={`# Compile policies into a bundle
opairctl bundle compile --input ./policies --output bundle.tar.gz

# Push bundle to registry
opairctl bundle push bundle.tar.gz --tag v1.0.0

# Pull bundle from registry
opairctl bundle pull my-bundle:v1.0.0

# List bundles
opairctl bundle list

# Inspect bundle contents
opairctl bundle inspect bundle.tar.gz`}
      />

      <h3>Deployment</h3>
      <CodeBlock
        title="opairctl deploy"
        language="bash"
        code={`# Deploy bundle to environment
opairctl deploy bundle.tar.gz --env production

# Dry-run deployment (preview changes)
opairctl deploy bundle.tar.gz --env production --dry-run

# Rollback deployment
opairctl deploy rollback --env production --version v0.9.0

# Check deployment status
opairctl deploy status --env production

# View deployment history
opairctl deploy history --env production`}
      />

      <h3>Audit Export</h3>
      <CodeBlock
        title="opairctl audit"
        language="bash"
        code={`# Export audit events as CSV
opairctl audit export --format csv --output audit-log.csv

# Export as JSON with date filter
opairctl audit export --format json --from 2026-01-01 --to 2026-03-31

# Filter by action type
opairctl audit export --format csv --action policy.updated --action workload.created

# Filter by user
opairctl audit export --format json --user-id user-123`}
      />

      <h3>System Status</h3>
      <CodeBlock
        title="opairctl status"
        language="bash"
        code={`# Check system health
opairctl status

# Detailed component status
opairctl status --verbose

# Output as JSON
opairctl status --json`}
      />

      <h3>Testing</h3>
      <CodeBlock
        title="opairctl test"
        language="bash"
        code={`# Run policy tests
opairctl test ./policies --verbose

# Run with specific test file
opairctl test --file test-cases.yaml

# Generate test coverage report
opairctl test --coverage`}
      />

      <h2>Global Options</h2>
      <table>
        <thead>
          <tr>
            <th>Option</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>--help, -h</code>
            </td>
            <td>Show help for a command</td>
          </tr>
          <tr>
            <td>
              <code>--version, -v</code>
            </td>
            <td>Show CLI version</td>
          </tr>
          <tr>
            <td>
              <code>--json</code>
            </td>
            <td>Output in JSON format</td>
          </tr>
          <tr>
            <td>
              <code>--quiet, -q</code>
            </td>
            <td>Suppress non-error output</td>
          </tr>
          <tr>
            <td>
              <code>--verbose</code>
            </td>
            <td>Show detailed output</td>
          </tr>
          <tr>
            <td>
              <code>--config</code>
            </td>
            <td>Path to config file</td>
          </tr>
          <tr>
            <td>
              <code>--context</code>
            </td>
            <td>Override active context for this command</td>
          </tr>
          <tr>
            <td>
              <code>--dry-run</code>
            </td>
            <td>Preview changes without applying (where applicable)</td>
          </tr>
        </tbody>
      </table>

      <h2>Environment Variables</h2>
      <CodeBlock
        language="bash"
        code={`# API credentials
export OPAIR_API_KEY=your-api-key
export OPAIR_ORG_ID=your-org-id

# Or use Personal Access Token
export OPAIR_TOKEN=pat_abc123...

# Custom API endpoint
export OPAIR_API_URL=https://api.opair.example.com

# Debug mode
export OPAIR_DEBUG=true`}
      />

      <div className="not-prose my-8 p-4 bg-purple-50 border border-purple-200 rounded-lg">
        <h3 className="text-lg font-semibold text-purple-900 mb-2">Pro Tips</h3>
        <ul className="text-purple-800 space-y-1 text-sm">
          <li>
            Use <code>opairctl completion bash</code> to enable shell autocompletion
          </li>
          <li>
            Pipe JSON output to <code>jq</code> for filtering:{' '}
            <code>opairctl policy list --json | jq '.items[]'</code>
          </li>
          <li>
            Use <code>--dry-run</code> flag to preview changes without applying them
          </li>
          <li>
            Export config before importing to another env:{' '}
            <code>opairctl config export | opairctl config import --dry-run</code>
          </li>
        </ul>
      </div>
    </article>
  );
}
